"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useClarityAuth } from "@/lib/auth/use-clarity-auth";
import { getCurrentReturnTo, storeReturnTo } from "@/lib/auth/return-to";
import { useClaritySession } from "@/lib/auth/auth-store";
import { createTenantHref, getValidTenantIds } from "@/lib/auth/tenant-access";

function FullPageStatus({
  eyebrow,
  title,
  body,
  action,
}: {
  eyebrow: string;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="border-border bg-surface shadow-card w-full max-w-2xl rounded-4xl border p-8 sm:p-10">
        <p className="text-label-md text-content-tertiary tracking-[0.16em] uppercase">
          {eyebrow}
        </p>
        <h1 className="font-display text-foreground mt-4 text-4xl font-bold tracking-[-0.02em] sm:text-5xl">
          {title}
        </h1>
        <p className="text-body-lg text-content-secondary mt-4 max-w-xl">
          {body}
        </p>
        {action ? <div className="mt-8">{action}</div> : null}
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="border-border bg-surface shadow-card w-full max-w-xl rounded-[2rem] border p-8">
        <p className="text-label-md text-content-tertiary tracking-[0.16em] uppercase">
          Loading Workspace
        </p>
        <h1 className="font-display text-foreground mt-4 text-4xl font-bold tracking-[-0.02em]">
          Preparing your practice shell.
        </h1>
        <div className="bg-surface-muted mt-8 h-2 overflow-hidden rounded-full">
          <div className="bg-action-primary h-full w-1/3 animate-pulse rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function AuthenticatedAppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { isAuthenticated, isLoading, loginWithRedirect, logout, user } =
    useClarityAuth();
  const session = useClaritySession();
  const workspaceTenantIds = getValidTenantIds(session.tenantIds);
  const [loginRedirectError, setLoginRedirectError] = useState<string | null>(
    null,
  );
  const hasTriggeredLoginRedirect = useRef(false);
  const pathSegments = pathname.split("/").filter(Boolean);
  const currentTenant = pathSegments[0] ?? "";
  // Tenant routes bypass the standard ClarityOS shell chrome so the copied
  // proof-of-concept workspace can render as-is inside auth. If the design is
  // later rebuilt with native ClarityOS layout patterns, this branch is where
  // you would remove the chromeless handoff.
  const isTenantWorkspaceRoute = workspaceTenantIds.includes(
    pathSegments[0] ?? "",
  );

  useEffect(() => {
    if (isAuthenticated) {
      hasTriggeredLoginRedirect.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isLoading || isAuthenticated || hasTriggeredLoginRedirect.current) {
      return;
    }

    const returnTo = getCurrentReturnTo();

    hasTriggeredLoginRedirect.current = true;
    storeReturnTo(returnTo);

    void loginWithRedirect({
      appState: { returnTo },
    }).catch((error) => {
      hasTriggeredLoginRedirect.current = false;
      setLoginRedirectError(
        error instanceof Error
          ? error.message
          : "ClarityOS could not launch the Auth0 login flow.",
      );
    });
  }, [isAuthenticated, isLoading, loginWithRedirect]);

  if (isLoading || (isAuthenticated && session.status === "loading")) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <FullPageStatus
        eyebrow="Authentication"
        title={
          loginRedirectError
            ? "We couldn't open the ClarityOS login."
            : "Redirecting to the ClarityOS login."
        }
        body={
          loginRedirectError ??
          "Launching your Auth0 sign-in so you can continue into the authenticated workspace."
        }
      />
    );
  }

  if (session.status === "error") {
    return (
      <FullPageStatus
        eyebrow="Session Error"
        title="We couldn't initialize your ClarityOS session."
        body={
          session.error ??
          "The auth shell could not decode your claims or acquire an access token."
        }
        action={
          <button
            className="border-border text-foreground hover:bg-surface-muted inline-flex items-center rounded-2xl border px-5 py-3 text-sm font-semibold transition-colors"
            onClick={() => window.location.reload()}
            type="button"
          >
            Try Again
          </button>
        }
      />
    );
  }

  if (isTenantWorkspaceRoute) {
    return (
      <div className="min-h-screen bg-[var(--surface-page)]">{children}</div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(24,132,249,0.12),transparent_26rem),radial-gradient(circle_at_bottom_right,rgba(255,69,0,0.10),transparent_22rem),var(--surface-page)]">
      <header className="border-border bg-surface/90 sticky top-0 z-20 border-b backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center">
          <div className="flex items-center gap-4">
            <Link href="/" className="flex items-center gap-3 no-underline">
              <div className="shadow-card flex size-12 items-center justify-center rounded-[1.25rem] bg-[linear-gradient(135deg,var(--primary),#ff5b2e)] text-sm font-bold tracking-[0.16em] text-white uppercase">
                CO
              </div>
              <div>
                <p className="text-label-md text-content-tertiary tracking-[0.18em] uppercase">
                  ClarityOS
                </p>
                <p className="text-content-secondary text-sm">
                  Authenticated workspace
                </p>
              </div>
            </Link>
          </div>

          <nav className="flex flex-wrap gap-2 lg:ml-auto">
            {workspaceTenantIds.map((tenantId) => {
              const isActive = tenantId === currentTenant;

              return (
                <Link
                  key={tenantId}
                  href={createTenantHref(tenantId)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "border-action-primary bg-action-primary text-action-primary-foreground"
                      : "border-border bg-surface text-content-secondary hover:bg-surface-muted hover:text-foreground"
                  }`}
                >
                  {tenantId}
                </Link>
              );
            })}
          </nav>

          <div className="border-border bg-surface shadow-card flex items-center justify-between gap-4 rounded-[1.5rem] border px-4 py-3 lg:min-w-72">
            <div className="min-w-0">
              <p className="text-foreground truncate text-sm font-semibold">
                {user?.email ?? user?.name ?? "Signed in"}
              </p>
              <p className="text-content-tertiary truncate text-xs">
                {session.roles[0] ?? "Authenticated ClarityOS user"}
              </p>
            </div>
            <button
              className="border-border text-foreground hover:bg-surface-muted rounded-full border px-4 py-2 text-sm font-medium transition-colors"
              onClick={() =>
                void logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                })
              }
              type="button"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8 sm:py-10">{children}</main>
    </div>
  );
}
